import { lazy, Suspense, useContext, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PageHeader from "../PageHeader";
import CopyButton from "../CopyButton";
import ThemeContext from "../../context/ThemeContext";
import { snippetApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { SNIPPET_TYPE } from "../../constants";
import { isAgent } from "../tasks/userDisplay";
import SharingControl from "../sharing/SharingControl";

// What each setting means for a snippet: shared snippets are read + copy,
// never edited by anyone outside its owner's side (household sharing Q5).
const SNIPPET_VISIBILITY_HINTS = {
  private: "Only you and your agents can see it.",
  household:
    "Your household — its people and their agents — can read and copy it.",
  people:
    "The people it's shared with — and their agents — can read and copy it.",
};

// Detail-page-only render libraries, split into their own chunks so the global
// bundle doesn't carry react-syntax-highlighter (+ grammars, ~95 KB) and
// react-markdown on every page. Each loads on demand when its snippet_type is
// viewed; the Suspense fallback shows the raw body as plain <pre> meanwhile.
const CodeBlock = lazy(() => import("./CodeBlock"));
const MarkdownBlock = lazy(() => import("./MarkdownBlock"));

// The type badge is `secondary` on the list and here alike — one variant
// for one meaning across sections (#34 review A).
const TYPE_BADGE_VARIANT = {};

function SnippetDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // The highlighter takes the theme by value (codeHighlight.pickPrismTheme).
  const { theme } = useContext(ThemeContext);
  // The store is shared across the library group, so this page can be
  // showing somebody else's snippet. AppShell resolves /users/me/ once and
  // hands it down, so this costs no extra request.
  const { me } = useOutletContext() || {};
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [copying, setCopying] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    snippetApi
      .get(slug)
      .then((response) => {
        if (!cancelled) {
          setSnippet(response.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.response?.status === 404 ? "notfound" : "load");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // A plain visibility flip: the page shows what went wrong.
  const changeVisibility = async (changes) => {
    setActionError(null);
    try {
      const { data } = await snippetApi.update(snippet.slug, changes);
      setSnippet(data);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't change who can see it."));
    }
  };

  // The Share dialog saves through here and shows its own errors.
  const share = async (changes) => {
    const { data } = await snippetApi.update(snippet.slug, changes);
    setSnippet(data);
  };

  const copyToMine = async () => {
    setCopying(true);
    setActionError(null);
    try {
      const { data } = await snippetApi.copy(snippet.slug);
      navigate(`/snippets/${data.slug}`);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't copy it. Try again."));
    } finally {
      setCopying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await snippetApi.delete(slug);
      navigate("/snippets");
    } catch (err) {
      // apiErrorMessage, not err.message: snippets are library-group
      // scoped now, so a 403 is reachable ("Only the library's owner can
      // delete snippets they did not create") and that DRF detail is the
      // only thing that explains WHY the button refused. Also the house
      // rule — mono-frontend-patterns forbids hand-reading err.response.
      setDeleteError(apiErrorMessage(err, "Failed to delete snippet"));
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (!deleting) {
      setShowDeleteDialog(false);
      setDeleteError(null);
    }
  };

  if (loading) {
    return (
      <div className="page-container mt-6 py-12 pt-4 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading snippet...</span>
      </div>
    );
  }

  if (loadError === "notfound") {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert className="mb-3">
          <AlertDescription>Snippet not found.</AlertDescription>
        </Alert>
        <Link
          to="/snippets"
          className="text-primary underline-offset-4 hover:underline"
        >
          Back to snippets
        </Link>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert variant="destructive">
          <AlertDescription>Failed to load snippet.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const plainBody = (
    <pre className="mb-0 font-mono text-sm whitespace-pre-wrap break-words">
      {snippet.body}
    </pre>
  );

  // `mine` drives attribution; `canDelete` mirrors the server's D5 rule —
  // you may delete what you created, and the group's HUMAN may delete
  // anything. A human is always their own library root, so "viewer is a
  // human" is exactly the server's `library_root(user) == user.pk`.
  // Unknown viewer (me still resolving) falls back to showing nothing
  // rather than mislabelling every row as somebody else's.
  const mine = !me || !snippet.user || snippet.user.id === me.id;
  // The server says what this caller may do (household sharing: a snippet
  // shared with you is read + copy). Older payloads without the flags keep
  // the group rule above.
  const canEdit = snippet.can_edit ?? true;
  const canDelete = snippet.can_delete ?? (mine || me?.kind === "human");

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: 880 }}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <PageHeader
          title={snippet.title || snippet.slug}
          subtitle={snippet.slug}
        />
        <div className="flex shrink-0 gap-2">
          {canEdit ? (
            <Button asChild variant="outline">
              <Link to={`/snippets/${snippet.slug}/edit`}>Edit</Link>
            </Button>
          ) : (
            <Button onClick={copyToMine} disabled={copying}>
              {copying && <Loader2 className="animate-spin" />}
              Copy to my snippets
            </Button>
          )}
          {/* Edit stays open to the whole group (D5: reads, creates and
              edits are shared) — only DELETE is bounded, so only Delete is
              hidden. Offering a button that is going to 403 and explaining
              afterwards is a worse version of the same information. */}
          {canDelete && (
            <Button
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {!mine && canEdit && snippet.user && (
        <div className="mb-2 text-sm text-muted-foreground">
          Saved by {snippet.user.username}
          {isAgent(snippet.user) && " (agent)"}
          {canEdit &&
            !canDelete &&
            " — only they or the library's owner can delete it"}
        </div>
      )}

      {!canEdit && (
        <p className="mb-2 text-sm text-muted-foreground">
          Shared with you by {snippet.user?.username ?? "its owner"} to read and
          copy — only their side can edit it. Copy it to make your own.
        </p>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {snippet.visibility && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SharingControl
            title={snippet.title || snippet.slug}
            selectLabel="Who can see this snippet"
            visibility={snippet.visibility}
            sharedWith={snippet.shared_with}
            ownerName={snippet.user?.username}
            canChangeVisibility={canEdit}
            canChangeSharing={Boolean(snippet.can_change_sharing)}
            hints={SNIPPET_VISIBILITY_HINTS}
            householdOption="Household — they can read and copy"
            shareDescription="The people you add — and their agents — can read it and copy it into their own snippets. Only your side can edit it or change who it's shared with."
            idPrefix="snippet"
            onChange={changeVisibility}
            onShare={share}
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge
          variant={TYPE_BADGE_VARIANT[snippet.snippet_type] || "secondary"}
          className="font-normal"
        >
          {snippet.snippet_type}
        </Badge>
        {snippet.language && (
          <Badge variant="outline" className="font-normal">
            {snippet.language}
          </Badge>
        )}
        <span className="text-sm text-muted-foreground">
          Updated {new Date(snippet.updated_at).toLocaleString()}
        </span>
        {/* The raw body (not the rendered markdown/highlighting) and the
            short id an agent or the MCP takes (board #82). */}
        <div className="ml-auto flex gap-2">
          <CopyButton value={snippet.body}>Copy text</CopyButton>
          <CopyButton value={snippet.slug}>Copy id</CopyButton>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-xs">
        {/* Three render paths, one per snippet_type:
            - markdown: react-markdown + rehype-sanitize (allowlist HTML,
              strips <script>, event handlers, javascript: URLs)
            - code: react-syntax-highlighter (Prism light variant, only the
              ~27 allowlist languages registered; see codeHighlight.js)
            - plain: <pre> with monospace + wrap
            markdown + code are lazy-loaded; `plainBody` doubles as their
            Suspense fallback so content is visible while the chunk loads. */}
        {snippet.snippet_type === SNIPPET_TYPE.MARKDOWN ? (
          <Suspense fallback={plainBody}>
            <MarkdownBlock body={snippet.body} />
          </Suspense>
        ) : snippet.snippet_type === SNIPPET_TYPE.CODE && snippet.language ? (
          <Suspense fallback={plainBody}>
            <CodeBlock
              code={snippet.body}
              language={snippet.language}
              theme={theme}
            />
          </Suspense>
        ) : (
          plainBody
        )}
      </div>

      {/* The delete confirm — the #28 dialog contract: static backdrop
          (clicking outside never closes it), Escape and the X disabled
          while the delete is in flight, focus returns to the Delete
          button that opened it. */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => !open && handleCancelDelete()}
      >
        <DialogContent
          showCloseButton={!deleting}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => deleting && event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Delete snippet
            </DialogTitle>
            <DialogDescription>
              Delete <strong>{snippet.title || snippet.slug}</strong>? This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SnippetDetail;
